import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { FAQAccordion } from '../components/FAQAccordion';
import { useFAQCategory } from '../hooks/useFAQ';

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const { t } = useTranslation('help');

  const { data, isLoading, error } = useFAQCategory(slug || '');

  // Check for highlighted item from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setHighlightedItemId(hash);
      // Clear highlight after animation
      setTimeout(() => setHighlightedItemId(null), 3000);
    }
  }, [location.hash]);

  if (isLoading) {
    return (
      <div className="min-h-full pb-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-white/10 rounded-[15px] w-1/3" />
          <div className="h-12 bg-white/10 rounded-[15px]" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="glass-effect rounded-[15px] h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 mb-4">{t('category.notFound')}</p>
          <Link
            to="/help"
            className="text-pp-orange hover:underline font-medium"
          >
            {t('category.backToOverview')}
          </Link>
        </div>
      </div>
    );
  }

  const { category, subcategories, items } = data;

  // Filter items by selected subcategory
  const filteredItems = selectedSubcategory
    ? items.filter(item => item.subcategory_id === selectedSubcategory)
    : items;

  // Group items by subcategory for display
  const itemsBySubcategory = subcategories.map(sub => ({
    subcategory: sub,
    items: items.filter(item => item.subcategory_id === sub.id)
  }));

  // Items without subcategory
  const generalItems = items.filter(item => !item.subcategory_id);

  return (
    <div className="min-h-full pb-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-white">{category.name}</h1>
        {category.description && (
          <p className="text-white/60 text-sm mt-1">{category.description}</p>
        )}
      </div>

      {/* Subcategory Tabs (if any) */}
      {subcategories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <button
            onClick={() => setSelectedSubcategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
                        transition-colors flex-shrink-0
                        ${!selectedSubcategory
                          ? 'bg-pp-orange text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/15'}`}
          >
            {t('category.all')}
          </button>
          {subcategories.map((sub) => (
            <button
              key={sub.id}
              onClick={() => setSelectedSubcategory(sub.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
                          transition-colors flex-shrink-0
                          ${selectedSubcategory === sub.id
                            ? 'bg-pp-orange text-white'
                            : 'bg-white/10 text-white/70 hover:bg-white/15'}`}
            >
              {sub.name}
            </button>
          ))}
        </div>
      )}

      {/* FAQ Items */}
      <div>
        {selectedSubcategory ? (
          // Show filtered items
          <div className="space-y-3">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <FAQAccordion
                  key={item.id}
                  item={item}
                  highlight={item.id === highlightedItemId}
                />
              ))
            ) : (
              <p className="text-center text-white/50 py-8">
                {t('category.noQuestions')}
              </p>
            )}
          </div>
        ) : subcategories.length > 0 ? (
          // Show grouped by subcategory
          <div className="space-y-8">
            {/* General items first */}
            {generalItems.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">
                  {t('category.general')}
                </h2>
                <div className="space-y-3">
                  {generalItems.map((item) => (
                    <FAQAccordion
                      key={item.id}
                      item={item}
                      highlight={item.id === highlightedItemId}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Subcategory groups */}
            {itemsBySubcategory.map(({ subcategory, items: subItems }) => (
              subItems.length > 0 && (
                <div key={subcategory.id}>
                  <h2 className="text-lg font-semibold text-white mb-4">
                    {subcategory.name}
                  </h2>
                  <div className="space-y-3">
                    {subItems.map((item) => (
                      <FAQAccordion
                        key={item.id}
                        item={item}
                        highlight={item.id === highlightedItemId}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          // No subcategories - show all items
          <div className="space-y-3">
            {items.length > 0 ? (
              items.map((item) => (
                <FAQAccordion
                  key={item.id}
                  item={item}
                  highlight={item.id === highlightedItemId}
                />
              ))
            ) : (
              <p className="text-center text-white/50 py-8">
                {t('category.noQuestionsInCategory')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Contact Hint */}
      <div className="glass-effect rounded-[15px] p-4 text-center">
        <p className="text-sm text-white/60 mb-2">
          {t('category.questionNotFound')}
        </p>
        <Link
          to="/help/contact"
          className="inline-flex items-center gap-2 text-sm font-medium text-pp-orange hover:underline"
        >
          <MessageSquare className="w-4 h-4" />
          {t('category.contactUs')}
        </Link>
      </div>
    </div>
  );
}
